import { Button } from "@goodfill/ui-components";
import { useNavigate } from "react-router-dom";
import { route } from "../../../../routes";
import {
  BondPriceComponent,
  FaceAmountComponent,
  ProfitAndLossComponent,
  TradeDateComponent,
  YearsHeldComponent,
  YearsToMaturityComponent,
} from "@goodfill/domain-properties-components";
import { PresentedPortfolioPosition } from "./PresentedPortfolioPosition";
import { sortByKeys, SortColumn } from "@goodfill/sort";
import { useMemo, useState } from "react";
import { getPortfolioPositionsComparator } from "./PortfolioPositionsComparator";
import { BatchSellButtons, BatchSellModal, BatchSellRowCheckbox } from "./batchSell/SelectedToBatchSellContext";
import {
  defaultPortfolioPositionsFilterConstraints,
  PortfolioPositionsFilter,
  portfolioPositionsFilters,
} from "./PortfolioPositionsFilter";
import DataGrid, { Column } from "react-data-grid";
import AutoSizer from "react-virtualized-auto-sizer";
import { mapSortColumnFromGrid } from "../../../../sort/mapSortColumnFromGrid";
import { GridTableFilterRow } from "../../../../ui-components/GridTable/GridTableFilterRow";
import { asAmount, asBondPriceNominal, asProfitAndLoss, Currency } from "@goodfill/domain-properties";
import { DateRangeFilter } from "../../../../filters/DateTimeRangeFilter";
import { DecimalRangeFilter } from "../../../../filters/DecimalRangeFilter";
import { applyFilters } from "../../../../filters/filters";
import { NumberRangeFilter } from "../../../../filters/NumberRangeFilter";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";
import { getSortedIsins, IsinColumnDefinition } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  getSortedIssuerIds,
  IssuerIdColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { MaturityDateColumnDefinition } from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { MoodysRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  getSortedPaymentRanks,
  PaymentRankColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import { SpRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import {
  getSortedTickers,
  TickerColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import {
  CouponPaymentColumnDefinition,
  getSortedBondCouponPayments,
} from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";

type SortableKeys = keyof PresentedPortfolioPosition;

export const PortfolioPositionsTable = ({ positionRows }: { positionRows: PresentedPortfolioPosition[] }) => {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState(defaultPortfolioPositionsFilterConstraints);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn<SortableKeys>[]>([]);

  const onFilterChange = <T extends keyof PortfolioPositionsFilter>(key: T, value: PortfolioPositionsFilter[T]) =>
    setCriteria({ ...criteria, [key]: value });

  const tableDefinition: Array<
    Column<PresentedPortfolioPosition> & {
      key: keyof PresentedPortfolioPosition | "_buttons";
    }
  > = useMemo(() => {
    const issuerIds = getSortedIssuerIds("issuerId", positionRows);
    const isins = getSortedIsins("bondId", positionRows);
    const tickers = getSortedTickers("ticker", positionRows);
    const paymentRanks = getSortedPaymentRanks("paymentRank", positionRows);
    const couponPayments = getSortedBondCouponPayments("couponPayment", positionRows);

    return [
      {
        key: "_buttons", // Action
        name: <BatchSellButtons rows={positionRows} />,
        sortable: false,
        formatter: ({ row }) => {
          const canBeSold = row.availableToSell.greaterThan(0);

          return canBeSold ? (
            <div className="flex flex-row items-center gap-2">
              <BatchSellRowCheckbox rowId={row.id} />
              <Button
                size="small"
                fill
                data-testid={`sell-${row.orderTicketId}`}
                red={true}
                className="w-24"
                onClick={() => navigate(route.orderSell(row.bondId, row.id))}
              >
                Sell
              </Button>
            </div>
          ) : (
            <></>
          );
        },
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 250,
      },
      {
        key: "tradeDate",
        name: "Trade Date",
        sortable: true,
        formatter: ({ row }) => <TradeDateComponent value={row.tradeDate} timezone={TIMEZONE_NEY_YORK} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DateRangeFilter
              small
              timezone={TIMEZONE_NEY_YORK}
              value={criteria.tradeDateRange}
              onChange={(value) => onFilterChange("tradeDateRange", value)}
            />
          </GridTableFilterRow>
        ),
        width: 320,
      },
      {
        key: "yearsHeld",
        name: "Years Held",
        sortable: true,
        formatter: ({ row }) => <YearsHeldComponent value={row.yearsHeld} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <NumberRangeFilter
              small
              label="Years Held"
              value={criteria.yearsHeldRange}
              onChange={(value) => onFilterChange("yearsHeldRange", value)}
            />
          </GridTableFilterRow>
        ),
        width: 150,
      },
      TickerColumnDefinition("ticker", criteria, onFilterChange, tickers),
      IssuerIdColumnDefinition("issuerId", criteria, onFilterChange, issuerIds),
      PaymentRankColumnDefinition("paymentRank", criteria, onFilterChange, paymentRanks),
      IsinColumnDefinition("bondId", criteria, onFilterChange, isins),
      CouponPaymentColumnDefinition("couponPayment", criteria, onFilterChange, couponPayments),
      MaturityDateColumnDefinition("maturityDate", criteria, onFilterChange),
      {
        key: "yearsToMaturity",
        name: "Years to Maturity",
        sortable: true,
        formatter: ({ row }) => <YearsToMaturityComponent value={row.yearsToMaturity} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      {
        key: "amount",
        name: (
          <>
            Face Amount
            <br />
            Original
          </>
        ),
        sortable: true,
        formatter: ({ row }) => <FaceAmountComponent value={row.amount} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      {
        key: "lockedInOrders",
        name: <>Face Amount Open Orders</>,
        sortable: true,
        formatter: ({ row }) => <FaceAmountComponent value={row.lockedInOrders} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={criteria.lockedInOrdersRange}
              onChange={(value) =>
                onFilterChange("lockedInOrdersRange", [
                  value[0] !== null ? asAmount(value[0]) : null,
                  value[1] !== null ? asAmount(value[1]) : null,
                ])
              }
              leftIcon="$"
            />
          </GridTableFilterRow>
        ),
        width: 300,
      },
      {
        key: "availableToSell",
        name: <>Face Amount Available to Sell</>,
        sortable: true,
        formatter: ({ row }) => <FaceAmountComponent value={row.availableToSell} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={criteria.availableToSellRange}
              onChange={(value) =>
                onFilterChange("availableToSellRange", [
                  value[0] !== null ? asAmount(value[0]) : null,
                  value[1] !== null ? asAmount(value[1]) : null,
                ])
              }
              leftIcon="$"
            />
          </GridTableFilterRow>
        ),
        width: 250,
      },
      {
        key: "loadedBuyPrice",
        name: <>Post-Fee Buy Price</>,
        sortable: true,
        formatter: ({ row }) => <BondPriceComponent value={row.loadedBuyPrice} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={[
                criteria.loadedBuyPriceRange[0] !== null ? criteria.loadedBuyPriceRange[0].mul(100) : null,
                criteria.loadedBuyPriceRange[1] !== null ? criteria.loadedBuyPriceRange[1].mul(100) : null,
              ]}
              onChange={(value) =>
                onFilterChange("loadedBuyPriceRange", [
                  value[0] !== null ? asBondPriceNominal(value[0].div(100)) : null,
                  value[1] !== null ? asBondPriceNominal(value[1].div(100)) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 150,
      },
      {
        key: "currentLoadedBidPrice",
        name: <>Post-Fee Bid Price</>,
        sortable: true,
        formatter: ({ row }) => <BondPriceComponent value={row.currentLoadedBidPrice} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      {
        key: "pnlRealized",
        name: <>P&L Realized</>,
        sortable: true,
        formatter: ({ row }) => <ProfitAndLossComponent value={row.pnlRealized} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={[
                criteria.pnlRealizedRange[0] !== null ? criteria.pnlRealizedRange[0].amount : null,
                criteria.pnlRealizedRange[1] !== null ? criteria.pnlRealizedRange[1].amount : null,
              ]}
              onChange={(value) =>
                onFilterChange("pnlRealizedRange", [
                  value[0] !== null ? asProfitAndLoss({ amount: asAmount(value[0]), currency: Currency.USD }) : null,
                  value[1] !== null ? asProfitAndLoss({ amount: asAmount(value[1]), currency: Currency.USD }) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 150,
      },
      {
        key: "plnUnrealizedInOrders",
        name: <>P&L Unrealized in Open Orders</>,
        sortable: true,
        formatter: ({ row }) => <ProfitAndLossComponent value={row.plnUnrealizedInOrders} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={[
                criteria.plnUnrealizedInOrdersRange[0] !== null ? criteria.plnUnrealizedInOrdersRange[0].amount : null,
                criteria.plnUnrealizedInOrdersRange[1] !== null ? criteria.plnUnrealizedInOrdersRange[1].amount : null,
              ]}
              onChange={(value) =>
                onFilterChange("plnUnrealizedInOrdersRange", [
                  value[0] !== null ? asProfitAndLoss({ amount: asAmount(value[0]), currency: Currency.USD }) : null,
                  value[1] !== null ? asProfitAndLoss({ amount: asAmount(value[1]), currency: Currency.USD }) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 250,
      },
      {
        key: "plnUnrealizedAvailableToSell",
        name: <>P&L Unrealized Available to Sell</>,
        sortable: true,
        formatter: ({ row }) => <ProfitAndLossComponent value={row.plnUnrealizedAvailableToSell} />,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DecimalRangeFilter
              small
              value={[
                criteria.plnUnrealizedAvailableToSellRange[0] !== null
                  ? criteria.plnUnrealizedAvailableToSellRange[0].amount
                  : null,
                criteria.plnUnrealizedAvailableToSellRange[1] !== null
                  ? criteria.plnUnrealizedAvailableToSellRange[1].amount
                  : null,
              ]}
              onChange={(value) =>
                onFilterChange("plnUnrealizedAvailableToSellRange", [
                  value[0] !== null ? asProfitAndLoss({ amount: asAmount(value[0]), currency: Currency.USD }) : null,
                  value[1] !== null ? asProfitAndLoss({ amount: asAmount(value[1]), currency: Currency.USD }) : null,
                ])
              }
            />
          </GridTableFilterRow>
        ),
        width: 250,
      },
      MoodysRatingColumnDefinition("bondRatingMoodys", criteria, onFilterChange),
      SpRatingColumnDefinition("bondRatingSp", criteria, onFilterChange),
    ];
  }, [criteria, onFilterChange, positionRows, navigate]);

  const filteredRows = applyFilters(positionRows, portfolioPositionsFilters, criteria);

  // TODO: abstract sortedRows/summaryRows into useSortedRows(rows) and useSummaryRows(rows), respectively
  const sortedRows = useMemo(
    () => sortByKeys(filteredRows, sortColumns, getPortfolioPositionsComparator),
    [filteredRows, sortColumns],
  );

  const isDark = false;

  return (
    <>
      <BatchSellModal rows={sortedRows} />
      <AutoSizer disableHeight>
        {({ height, width }) => (
          <DataGrid
            className={`rdg-copyable ${isDark ? "rdg-dark" : "rdg-light"} OrderListTable custom-scrollbar`}
            columns={tableDefinition}
            rows={sortedRows}
            headerRowHeight={75}
            sortColumns={sortColumns}
            onSortColumnsChange={(columns) => setSortColumns(columns.map(mapSortColumnFromGrid<SortableKeys>))}
            style={{ height: 700, width }}
          />
        )}
      </AutoSizer>
    </>
  );
};
